import { useAuth, useUser, SignIn } from '@clerk/clerk-react';
import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

type AuthState = 'loading' | 'signing-in' | 'getting-token' | 'success' | 'error';

/**
 * Page component that handles CLI authentication by obtaining a Clerk JWT token
 * and redirecting to the CLI's local callback server. Displays sign-in UI if
 * the user is not already authenticated.
 * @returns The rendered CLI authentication page.
 */
export function CliAuthPage() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<AuthState>('loading');
  const [error, setError] = useState('');

  const callbackUrl = searchParams.get('callback');

  const handleAuth = useCallback(async () => {
    if (!callbackUrl) {
      setState('error');
      setError('Missing callback parameter. Please run `tours auth` from the CLI.');
      return;
    }

    setState('getting-token');

    try {
      let token: string | null = null;
      try {
        token = await getToken({ template: 'cli' });
      } catch {
        token = await getToken();
      }

      if (!token) {
        setState('error');
        setError('Failed to get authentication token.');
        return;
      }

      // Redirect to CLI's local callback server with the token
      const redirectUrl = `${callbackUrl}?token=${encodeURIComponent(token)}`;
      window.location.href = redirectUrl;
      setState('success');
    } catch (err) {
      setState('error');
      setError(err instanceof Error ? err.message : 'Failed to get token');
    }
  }, [callbackUrl, getToken]);

  useEffect(() => {
    if (!isLoaded) return;

    if (isSignedIn) {
      handleAuth();
    } else {
      setState('signing-in');
    }
  }, [isLoaded, isSignedIn, handleAuth]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50">
      <div className="w-full max-w-md space-y-6 p-6 text-center">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">Tours CLI</h1>
          <p className="mt-1 text-sm text-zinc-500">Authenticate the command-line interface</p>
        </div>

        {state === 'loading' && (
          <div className="rounded-lg border bg-white p-6">
            <p className="text-zinc-500">Loading...</p>
          </div>
        )}

        {state === 'signing-in' && (
          <div className="flex justify-center">
            <SignIn
              afterSignInUrl={`/cli-auth?callback=${encodeURIComponent(callbackUrl || '')}`}
              routing="hash"
            />
          </div>
        )}

        {state === 'getting-token' && (
          <div className="rounded-lg border bg-white p-6">
            <p className="text-zinc-500">Authenticating...</p>
          </div>
        )}

        {state === 'success' && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-6">
            <p className="font-medium text-green-800">Authenticated successfully!</p>
            <p className="mt-1 text-sm text-green-600">
              {user?.firstName ? `Signed in as ${user.firstName} ${user.lastName}. ` : ''}
              You can close this tab and return to the terminal.
            </p>
          </div>
        )}

        {state === 'error' && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-6">
            <p className="font-medium text-red-800">Authentication failed</p>
            <p className="mt-1 text-sm text-red-600">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
